import { useState, useEffect } from "react";
import { Bar, Pie } from "react-chartjs-2";
import { useGlobalContext } from "../../GlobalContext";
import {
  Truck,
  PieChart as PieIcon,
  BadgeCheck,
  XCircle,
  Loader,
  AlertCircle,
  Send,
  Download,
  BarChart2,
  Store,
  TrendingUp,
  Activity,
  ArrowUpRight,
  CircleAlert,
  Trophy,
  Search,
  ArrowUpDown,
  Medal,
  ShieldAlert,
  FileWarning,
  Phone,
  Mail,
  Bike,
  Clock3,
  CalendarDays,
  LogIn,
} from "lucide-react";
import { exportAnyJsonToExcel } from "../Utils/exportAnyJsonToExcel";
import Logo from "../../Images/Packky.svg";
import LoadingScreen from "../Utils/LoadingScreen";
import { useNavigate } from "react-router-dom";

import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import TablePagination from "../Utils/TablePagination";
import useReports from "../../Sevices/useReports";
import useDriver from "../../Sevices/Driver Api/useDriver";
import { toast } from "sonner";

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
);

const ReportsDashboard = () => {
  const { accessToken, admin } = useGlobalContext();
  const navigate = useNavigate();

  const { fetchPackkyData, loading, loadingDrivers } = useReports();

  const { drivers, fetchDrivers } = useDriver();

  const today = new Date().toISOString().split("T")[0];

  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);

  const [restaurantData, setRestaurantData] = useState([]);
  const [driverTaskAnalytics, setDriverTaskAnalytics] = useState([]);
  const [taskStatusAnalytics, setTaskStatusAnalytics] = useState([]);
  const [activeTab, setActiveTab] = useState("packky");
  const [search, setSearch] = useState("");
  const [pendingAction, setPendingAction] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [shopSearch, setShopSearch] = useState("");
  const [driverSearchPackky, setDriverSearchPackky] = useState("");
  const [shopSortBy, setShopSortBy] = useState("tasks_desc");
  const [driverSortByPackky, setDriverSortByPackky] = useState("tasks_desc");
  const renderPagination = () => {
    const totalCount = filteredDrivers?.length || 0;

    if (totalCount <= driversPerPage) return null;

    return (
      <TablePagination
        currentPage={currentPage}
        totalCount={totalCount}
        pageSize={driversPerPage}
        onPageChange={setCurrentPage}
      />
    );
  };

  const handlePackkyExport = () => {
    if (
      taskStatusAnalytics.length === 0 &&
      restaurantData.length === 0 &&
      driverTaskAnalytics.length === 0
    ) {
      toast.error("No Packky report data available to export");
      return;
    }

    exportAnyJsonToExcel({
      data: taskStatusAnalytics,
      filePrefix: `Packky_Task_Status_${fromDate}_to_${toDate}`,
      sheetName: "Task Status",
    });

    exportAnyJsonToExcel({
      data: restaurantData,
      filePrefix: `Packky_Restaurant_Tasks_${fromDate}_to_${toDate}`,
      sheetName: "Restaurant Tasks",
    });

    exportAnyJsonToExcel({
      data: driverTaskAnalytics,
      filePrefix: `Packky_Driver_Tasks_${fromDate}_to_${toDate}`,
      sheetName: "Driver Tasks",
    });
  };

  const filteredDrivers = drivers?.filter((driver) => {
    if (!search) return true;

    const q = search.toLowerCase();

    return (
      driver.fullName?.toLowerCase().includes(q) ||
      driver.first_name?.toLowerCase().includes(q) ||
      driver.last_name?.toLowerCase().includes(q) ||
      driver.username?.toLowerCase().includes(q) ||
      driver.driverId?.toLowerCase().includes(q) ||
      driver.email?.toLowerCase().includes(q) ||
      driver.phone?.toLowerCase().includes(q) ||
      driver.teamName?.toLowerCase().includes(q)
    );
  });

  const driversPerPage = 15;
  const [currentPage, setCurrentPage] = useState(1);
  const indexOfLast = currentPage * driversPerPage;
  const indexOfFirst = indexOfLast - driversPerPage;
  const totalPages = Math.ceil(filteredDrivers?.length / driversPerPage);
  const currentDrivers = filteredDrivers?.slice(indexOfFirst, indexOfLast);

  const getStatusConfig = (status) => {
    const statusLower = status.toLowerCase();
    if (
      ["completed", "successful", "delivered", "arrived"].includes(statusLower)
    ) {
      return { Icon: BadgeCheck, color: "#059669" };
    }
    if (
      ["failed", "cancelled", "canceled", "error", "rejected"].includes(
        statusLower,
      )
    ) {
      return { Icon: XCircle, color: "#dc2626" };
    }
    if (["food is ready"].includes(statusLower)) {
      return { Icon: Send, color: "#14b8a6" };
    }
    if (["declined", "unassigned"].includes(statusLower)) {
      return { Icon: XCircle, color: "#a855f7" };
    }
    if (["pending", "inprogress", "assigned"].includes(statusLower)) {
      return { Icon: Loader, color: "#2563eb" };
    }
    if (["sent", "dispatched"].includes(statusLower)) {
      return { Icon: Send, color: "#f59e0b" };
    }
    return { Icon: AlertCircle, color: "#6b7280" };
  };

  useEffect(() => {
    fetchDrivers();
  }, []);

  useEffect(() => {
    if (admin?.userId && accessToken) {
      fetchPackkyData(
        fromDate,
        toDate,
        setRestaurantData,
        setDriverTaskAnalytics,
        setTaskStatusAnalytics,
      );
    }
  }, [admin, accessToken]);



  const handleOpen = () => {
    if (!selectedDriver || !pendingAction) return;

    const driverId = selectedDriver.driverId;
    const username = encodeURIComponent(
      selectedDriver.username || selectedDriver.fullName,
    );

    if (pendingAction === "analytics") {
      navigate(`/analytics/${driverId}/${username}`);
    } else if (pendingAction === "report") {
      navigate(`/analytics/${driverId}/${username}`, {
        state: {
          fromDate,
          toDate,
        },
      });
    }

    setConfirmOpen(false);
    setPendingAction(null);
    setSelectedDriver(null);
  };
  const safeRestaurantData = (restaurantData || []).map((r) => ({
    ...r,
    shopName: r?.shopName?.trim() || "Unknown Shop",
    shopCode: r?.shopCode || "—",
    taskCount: Number(r?.taskCount || 0),
  }));

  const safeDriverTaskAnalytics = (driverTaskAnalytics || []).map((d) => ({
    ...d,
    driverName: d?.driverName?.trim() || "Unknown Driver",
    driverCode: d?.driverCode || "—",
    driverMobile: d?.driverMobile || "—",
    taskCount: Number(d?.taskCount || 0),
  }));

  const safeTaskStatusAnalytics = (taskStatusAnalytics || []).map((s) => ({
    ...s,
    taskStatus: s?.taskStatus?.trim() || "unknown",
    taskCount: Number(s?.taskCount || 0),
  }));

  const totalRestaurantTasks = safeRestaurantData.reduce(
    (sum, item) => sum + item.taskCount,
    0,
  );

  const totalDriverTasks = safeDriverTaskAnalytics.reduce(
    (sum, item) => sum + item.taskCount,
    0,
  );

  const totalStatusTasks = safeTaskStatusAnalytics.reduce(
    (sum, item) => sum + item.taskCount,
    0,
  );

  const successfulTasks =
    safeTaskStatusAnalytics.find(
      (s) => s.taskStatus.toLowerCase() === "successful",
    )?.taskCount || 0;

  const cancelledTasks =
    safeTaskStatusAnalytics.find(
      (s) => s.taskStatus.toLowerCase() === "cancelled",
    )?.taskCount || 0;

  const activePipelineTasks = safeTaskStatusAnalytics
    .filter((s) =>
      ["acknowledged", "arrived", "food is ready", "assigned", "unassigned"].includes(
        s.taskStatus.toLowerCase(),
      ),
    )
    .reduce((sum, item) => sum + item.taskCount, 0);

  const successRate = totalStatusTasks
    ? ((successfulTasks / totalStatusTasks) * 100).toFixed(2)
    : "0.00";

  const cancelRate = totalStatusTasks
    ? ((cancelledTasks / totalStatusTasks) * 100).toFixed(2)
    : "0.00";

  const topShops = [...safeRestaurantData]
    .sort((a, b) => b.taskCount - a.taskCount)
    .slice(0, 5);

  const topDrivers = [...safeDriverTaskAnalytics]
    .sort((a, b) => b.taskCount - a.taskCount)
    .slice(0, 5);

  const busiestShop = topShops[0];
  const secondBusiestShop = topShops[1];

  const top2ShopShare = totalRestaurantTasks
    ? (
      (((busiestShop?.taskCount || 0) + (secondBusiestShop?.taskCount || 0)) /
        totalRestaurantTasks) *
      100
    ).toFixed(1)
    : "0.0";

  const avgTasksPerShop = safeRestaurantData.length
    ? Math.round(totalRestaurantTasks / safeRestaurantData.length)
    : 0;

  const avgTasksPerDriver = safeDriverTaskAnalytics.length
    ? Math.round(totalDriverTasks / safeDriverTaskAnalytics.length)
    : 0;

  const unknownShopCount = safeRestaurantData.filter(
    (item) => item.shopName === "Unknown Shop" || item.shopCode === "—",
  ).length;

  const unknownDriverCount = safeDriverTaskAnalytics.filter(
    (item) =>
      item.driverName === "Unknown Driver" ||
      item.driverCode === "—" ||
      item.driverMobile === "—",
  ).length;

  const topPerformerDriver = [...safeDriverTaskAnalytics].sort(
    (a, b) => b.taskCount - a.taskCount,
  )[0];

  const lowestPerformerDriver = [...safeDriverTaskAnalytics]
    .filter((d) => d.taskCount > 0)
    .sort((a, b) => a.taskCount - b.taskCount)[0];

  const restaurantPieChartData = {
    labels: topShops.map((r) => r.shopName),
    datasets: [
      {
        data: topShops.map((r) => r.taskCount),
        backgroundColor: ["#f97316", "#fb923c", "#fdba74", "#f59e0b", "#ea580c"],
        borderWidth: 0,
      },
    ],
  };

  const driverBarChartData = {
    labels: topDrivers.map((d) => d.driverName),
    datasets: [
      {
        label: "Tasks",
        data: topDrivers.map((d) => d.taskCount),
        backgroundColor: ["#f97316", "#fb923c", "#fdba74", "#f59e0b", "#ea580c"],
        borderRadius: 8,
        maxBarThickness: 40,
      },
    ],
  };

  const statusChartData = {
    labels: safeTaskStatusAnalytics.map((s) => s.taskStatus),
    datasets: [
      {
        label: "Task Count",
        data: safeTaskStatusAnalytics.map((s) => s.taskCount),
        backgroundColor: safeTaskStatusAnalytics.map((s) => {
          const status = s.taskStatus.toLowerCase();
          if (["successful", "completed", "delivered"].includes(status)) return "#16a34a";
          if (["cancelled", "declined", "failed"].includes(status)) return "#dc2626";
          if (["food is ready"].includes(status)) return "#f59e0b";
          if (["acknowledged", "assigned"].includes(status)) return "#f97316";
          if (["arrived"].includes(status)) return "#fb923c";
          return "#94a3b8";
        }),
        borderRadius: 8,
      },
    ],
  };

  const getShopShare = (count) =>
    totalRestaurantTasks ? ((count / totalRestaurantTasks) * 100).toFixed(1) : "0.0";

  const getDriverShare = (count) =>
    totalDriverTasks ? ((count / totalDriverTasks) * 100).toFixed(1) : "0.0";

  const sortShops = (list) => {
    const arr = [...list];
    switch (shopSortBy) {
      case "tasks_asc":
        return arr.sort((a, b) => a.taskCount - b.taskCount);
      case "name_asc":
        return arr.sort((a, b) => a.shopName.localeCompare(b.shopName));
      case "name_desc":
        return arr.sort((a, b) => b.shopName.localeCompare(a.shopName));
      case "tasks_desc":
      default:
        return arr.sort((a, b) => b.taskCount - a.taskCount);
    }
  };

  const sortDriversPackky = (list) => {
    const arr = [...list];
    switch (driverSortByPackky) {
      case "tasks_asc":
        return arr.sort((a, b) => a.taskCount - b.taskCount);
      case "name_asc":
        return arr.sort((a, b) => a.driverName.localeCompare(b.driverName));
      case "name_desc":
        return arr.sort((a, b) => b.driverName.localeCompare(a.driverName));
      case "tasks_desc":
      default:
        return arr.sort((a, b) => b.taskCount - a.taskCount);
    }
  };

  const filteredShopRows = sortShops(
    safeRestaurantData.filter((shop) => {
      const q = shopSearch.trim().toLowerCase();
      if (!q) return true;
      return (
        shop.shopName.toLowerCase().includes(q) ||
        shop.shopCode.toLowerCase().includes(q)
      );
    }),
  );

  const filteredDriverRowsPackky = sortDriversPackky(
    safeDriverTaskAnalytics.filter((driver) => {
      const q = driverSearchPackky.trim().toLowerCase();
      if (!q) return true;
      return (
        driver.driverName.toLowerCase().includes(q) ||
        driver.driverCode.toLowerCase().includes(q) ||
        driver.driverMobile.toLowerCase().includes(q)
      );
    }),
  );
  const formatDateTime = (value) => {
    if (!value) return "-";
    try {
      const date = new Date(value);
      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return value;
    }
  };

  const InfoBadge = ({ children, color = "gray" }) => {
    const styles = {
      gray: "bg-gray-100 text-gray-600",
      blue: "bg-blue-100 text-blue-700",
      green: "bg-green-100 text-green-700",
      orange: "bg-orange-100 text-orange-700",
      red: "bg-red-100 text-red-700",
      purple: "bg-purple-100 text-purple-700",
    };

    return (
      <span
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${styles[color]}`}
      >
        {children}
      </span>
    );
  };
  const renderDriversTable = () => (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-orange-400 text-white border-b border-gray-200">
            <tr className="text-left">
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                Driver
              </th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                Contact
              </th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                Vehicle / Shift
              </th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                Team / Type
              </th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                Status
              </th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                Activity
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100 bg-white">
            {loadingDrivers ? (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <Loader className="w-5 h-5 animate-spin text-blue-600" />
                    <span className="text-gray-600">Loading drivers...</span>
                  </div>
                </td>
              </tr>
            ) : currentDrivers?.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                  No drivers found
                </td>
              </tr>
            ) : (
              currentDrivers.map((driver) => (
                <tr
                  key={driver.id || driver.driverId}
                  className="align-top transition-colors duration-200 hover:bg-orange-50/40"
                >
                  {/* Driver */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <img
                        src={driver.profile_photo || "/placeholder-driver.png"}
                        alt={driver.fullName || "Driver"}
                        onError={(e) => {
                          e.currentTarget.src =
                            "https://ui-avatars.com/api/?name=" +
                            encodeURIComponent(driver.fullName || "Driver") +
                            "&background=F3F4F6&color=374151";
                        }}
                        className="h-11 w-11 rounded-xl border border-gray-200 object-cover shrink-0"
                      />

                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-gray-900 leading-none truncate">
                            {driver.fullName || "-"}
                          </p>
                          <InfoBadge color="blue">
                            {driver.driverId || "-"}
                          </InfoBadge>
                        </div>

                        <p className="mt-1 text-xs text-gray-500 truncate">
                          @{driver.username || "-"}
                        </p>
                      </div>
                    </div>
                  </td>

                  {/* Contact */}
                  <td className="px-4 py-3">
                    <div className="space-y-1 text-xs text-gray-700">
                      <div className="flex items-center gap-2 min-w-0">
                        <Phone size={13} className="text-gray-400 shrink-0" />
                        <span className="truncate">{driver.phone || "-"}</span>
                      </div>

                      <div className="flex items-center gap-2 min-w-0">
                        <Mail size={13} className="text-gray-400 shrink-0" />
                        <span className="truncate">{driver.email || "-"}</span>
                      </div>
                    </div>
                  </td>

                  {/* Vehicle / Shift */}
                  <td className="px-4 py-3">
                    <div className="space-y-1 text-xs text-gray-700">
                      <div className="flex items-center gap-2 min-w-0">
                        <Bike size={13} className="text-gray-400 shrink-0" />
                        <span className="truncate">
                          {driver.transport_type || "-"}
                          {driver.transport_description
                            ? ` • ${driver.transport_description}`
                            : ""}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 min-w-0">
                        <BadgeCheck size={13} className="text-gray-400 shrink-0" />
                        <span className="truncate">
                          {driver.licence_plate || "-"}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 min-w-0">
                        <Clock3 size={13} className="text-gray-400 shrink-0" />
                        <span>
                          {driver.shift_start || "-"} - {driver.shift_end || "-"}
                        </span>
                      </div>
                    </div>
                  </td>

                  {/* Team / Type */}
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      <InfoBadge color="orange">
                        {driver.teamName || "Unassigned"}
                      </InfoBadge>
                      <InfoBadge color="green">
                        {driver.driver_type || "N/A"}
                      </InfoBadge>
                      <InfoBadge color="purple">
                        {driver.tier || "N/A"}
                      </InfoBadge>
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-2">
                      <div>
                        <InfoBadge
                          color={
                            driver.dutyStatus === "active" ? "green" : "gray"
                          }
                        >
                          {driver.dutyStatus === "active"
                            ? "On duty"
                            : "Off duty"}
                        </InfoBadge>
                      </div>

                      <div>
                        <InfoBadge
                          color={driver.status === "active" ? "green" : "red"}
                        >
                          {driver.status || "-"}
                        </InfoBadge>
                      </div>
                    </div>
                  </td>

                  {/* Activity */}
                  <td className="px-4 py-3">
                    <div className="space-y-1 text-xs text-gray-700">
                      <div className="flex items-center gap-2">
                        <CalendarDays
                          size={13}
                          className="text-gray-400 shrink-0"
                        />
                        <span className="truncate">
                          {formatDateTime(driver.joiningDate)}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <LogIn size={13} className="text-gray-400 shrink-0" />
                        <span className="truncate">
                          {formatDateTime(driver.last_login)}
                        </span>
                      </div>
                    </div>
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1.5">
                      <button
                        className="inline-flex items-center gap-1 rounded-xl border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-blue-600 transition hover:bg-blue-50"
                        onClick={() => {
                          setSelectedDriver(driver);
                          setPendingAction("analytics");
                          setConfirmOpen(true);
                        }}
                        title="Open Analytics"
                      >
                        <BarChart2 size={14} />
                        Analytics
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
  const formatStatusLabel = (value = "") =>
  value
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const getStatusTone = (status = "") => {
  const s = status.toLowerCase();

  if (["successful", "completed", "delivered"].includes(s)) {
    return {
      color: "#16a34a",
      bg: "#dcfce7",
      border: "#bbf7d0",
      Icon: BadgeCheck,
      label: "Healthy",
    };
  }

  if (["cancelled", "canceled", "declined", "failed", "rejected"].includes(s)) {
    return {
      color: "#dc2626",
      bg: "#fee2e2",
      border: "#fecaca",
      Icon: XCircle,
      label: "Needs attention",
    };
  }

  if (["food is ready"].includes(s)) {
    return {
      color: "#f59e0b",
      bg: "#fef3c7",
      border: "#fde68a",
      Icon: Send,
      label: "Ready",
    };
  }

  if (["assigned", "acknowledged", "arrived"].includes(s)) {
    return {
      color: "#f97316",
      bg: "#ffedd5",
      border: "#fed7aa",
      Icon: Activity,
      label: "In progress",
    };
  }

  if (["unassigned"].includes(s)) {
    return {
      color: "#9333ea",
      bg: "#f3e8ff",
      border: "#e9d5ff",
      Icon: CircleAlert,
      label: "Open",
    };
  }

  return {
    color: "#64748b",
    bg: "#f1f5f9",
    border: "#e2e8f0",
    Icon: AlertCircle,
    label: "Other",
  };
};

const statusOrder = [
  "successful",
  "completed",
  "delivered",
  "assigned",
  "acknowledged",
  "arrived",
  "food is ready",
  "unassigned",
  "cancelled",
  "canceled",
  "declined",
  "failed",
  "rejected",
];

const statusDisplayRows = [...safeTaskStatusAnalytics]
  .map((item) => {
    const percentage = totalStatusTasks
      ? ((item.taskCount / totalStatusTasks) * 100).toFixed(1)
      : "0.0";

    return {
      ...item,
      percentage,
      tone: getStatusTone(item.taskStatus),
    };
  })
  .sort((a, b) => {
    const aIndex = statusOrder.indexOf(a.taskStatus.toLowerCase());
    const bIndex = statusOrder.indexOf(b.taskStatus.toLowerCase());

    return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
  });

const topStatus = [...statusDisplayRows].sort(
  (a, b) => b.taskCount - a.taskCount,
)[0];

const statusSummaryCards = [
  {
    label: "Successful",
    value: successfulTasks,
    percentage: successRate,
    Icon: BadgeCheck,
    color: "#16a34a",
    bg: "#dcfce7",
  },
  {
    label: "Active Pipeline",
    value: activePipelineTasks,
    percentage: totalStatusTasks
      ? ((activePipelineTasks / totalStatusTasks) * 100).toFixed(1)
      : "0.0",
    Icon: Activity,
    color: "#f97316",
    bg: "#ffedd5",
  },
  {
    label: "Cancelled",
    value: cancelledTasks,
    percentage: cancelRate,
    Icon: XCircle,
    color: "#dc2626",
    bg: "#fee2e2",
  },
];

  return (
    <>
      <div className="p-6 min-h-screen space-y-6 animate-fade-in">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <img src={Logo} alt="Logo" className="h-10" />
            <p className="text-2xl font-bold ps-3">
              {activeTab === "driver" ? "Driver Report" : "Report"}
            </p>
          </div>
        </div>

        {loading ? (
          <LoadingScreen />
        ) : (
          <>
            <div className="mb-4 border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab("packky")}
                  className={`py-3 px-1 border-b-2 text-sm font-medium ${activeTab === "packky"
                    ? "border-orange-600 text-orange-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                    }`}
                >
                  Packky Reports
                </button>
                <button
                  onClick={() => setActiveTab("driver")}
                  className={`py-3 px-1 border-b-2 text-sm font-medium ${activeTab === "driver"
                    ? "border-orange-600 text-orange-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                    }`}
                >
                  Driver Reports
                </button>
              </nav>
            </div>

            {loading ? (
              <LoadingScreen />
            ) : (
              <>
                {activeTab === "driver" && (
                  <>
                    <div className="flex flex-wrap md:flex-nowrap gap-3 mb-4 items-end">
                      <input
                        type="text"
                        placeholder="Search by name, code, email, phone..."
                        value={search}
                        onChange={(e) => {
                          setSearch(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="px-3 py-2 border rounded text-sm w-full md:w-64"
                      />



                      <div className="flex gap-2 ml-auto">
                        <button
                          className="min-w-[96px] h-10 px-3 py-1 bg-green-500 text-white rounded"
                          onClick={() => {
                            fetchPackkyData(
                              fromDate,
                              toDate,
                              setRestaurantData,
                              setDriverTaskAnalytics,
                              setTaskStatusAnalytics,
                            );
                            fetchDrivers();
                          }}
                        >
                          Refresh
                        </button>
                      </div>
                    </div>

                    {renderDriversTable()}
                    {renderPagination()}
                  </>
                )}

                {activeTab === "packky" && (
                  <>
                    {/* Top Controls */}
                    <div className="bg-white rounded-2xl border shadow-sm p-4 mb-4">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                        <div>
                          <label className="block text-sm font-medium mb-1 text-gray-700">
                            From Date
                          </label>
                          <input
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            className="h-11 px-3 border rounded-xl w-full text-sm outline-none focus:ring-2 focus:ring-orange-200"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-1 text-gray-700">
                            To Date
                          </label>
                          <input
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            className="h-11 px-3 border rounded-xl w-full text-sm outline-none focus:ring-2 focus:ring-orange-200"
                          />
                        </div>

                        <button
                          onClick={() =>
                            fetchPackkyData(
                              fromDate,
                              toDate,
                              setRestaurantData,
                              setDriverTaskAnalytics,
                              setTaskStatusAnalytics,
                            )
                          }
                          className="h-11 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium"
                        >
                          Apply Filter
                        </button>

                        <button
                          className="h-11 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm flex items-center justify-center gap-2 font-medium"
                          onClick={handlePackkyExport}
                        >
                          <Download size={16} />
                          Export Report
                        </button>
                      </div>
                    </div>

                    {/* KPI Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 mb-5">
                      <div className="bg-white rounded-2xl border shadow-sm p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-sm text-gray-500">Total Tasks</p>
                            <h3 className="text-3xl font-bold text-gray-900 mt-1">
                              {totalStatusTasks}
                            </h3>
                          </div>
                          <div className="w-11 h-11 rounded-xl bg-orange-50 flex items-center justify-center">
                            <Activity className="w-5 h-5 text-orange-600" />
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-3">
                          Status-based overall task volume
                        </p>
                      </div>

                      <div className="bg-white rounded-2xl border shadow-sm p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-sm text-gray-500">Success Rate</p>
                            <h3 className="text-3xl font-bold text-green-600 mt-1">
                              {successRate}%
                            </h3>
                          </div>
                          <div className="w-11 h-11 rounded-xl bg-green-50 flex items-center justify-center">
                            <BadgeCheck className="w-5 h-5 text-green-600" />
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-3">
                          {successfulTasks} successful tasks
                        </p>
                      </div>

                      <div className="bg-white rounded-2xl border shadow-sm p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-sm text-gray-500">Cancelled</p>
                            <h3 className="text-3xl font-bold text-red-600 mt-1">
                              {cancelledTasks}
                            </h3>
                          </div>
                          <div className="w-11 h-11 rounded-xl bg-red-50 flex items-center justify-center">
                            <XCircle className="w-5 h-5 text-red-600" />
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-3">
                          {cancelRate}% of total status volume
                        </p>
                      </div>

                      <div className="bg-white rounded-2xl border shadow-sm p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-sm text-gray-500">Active Pipeline</p>
                            <h3 className="text-3xl font-bold text-orange-600 mt-1">
                              {activePipelineTasks}
                            </h3>
                          </div>
                          <div className="w-11 h-11 rounded-xl bg-orange-50 flex items-center justify-center">
                            <Loader className="w-5 h-5 text-orange-600" />
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-3">
                          Acknowledged / arrived / ready / unassigned
                        </p>
                      </div>

                      <div className="bg-white rounded-2xl border shadow-sm p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-sm text-gray-500">Shop Concentration</p>
                            <h3 className="text-3xl font-bold text-orange-600 mt-1">
                              {top2ShopShare}%
                            </h3>
                          </div>
                          <div className="w-11 h-11 rounded-xl bg-orange-50 flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-orange-600" />
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-3">
                          Share of top 2 shops in total shop volume
                        </p>
                      </div>
                    </div>

                    {/* Hero + Attention */}
                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-5">
                      <div className="xl:col-span-2 rounded-[28px] p-6 md:p-7 shadow-[0_10px_30px_rgba(252,152,27,0.18)] border border-orange-200 bg-gradient-to-br from-[#FC981B] via-[#F59E0B] to-[#F97316] text-white relative overflow-hidden">
                        {/* decorative glow */}
                        <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-white/10 blur-3xl" />
                        <div className="absolute -bottom-12 -left-12 h-44 w-44 rounded-full bg-white/10 blur-3xl" />

                        <div className="relative z-10 flex items-start justify-between gap-4">
                          <div>
                            <p className="text-sm font-medium text-white/80 mb-2 tracking-wide">
                              Quick Overview
                            </p>

                            <h3 className="text-2xl md:text-3xl font-bold mb-3 leading-tight">
                              Packky task performance overview
                            </h3>

                            <p className="text-sm md:text-[15px] text-white/90 leading-7 max-w-3xl">
                              Delivery performance is strong with{" "}
                              <span className="font-semibold text-white">{successRate}%</span>{" "}
                              successful tasks and only{" "}
                              <span className="font-semibold text-white">{cancelRate}%</span>{" "}
                              cancellations in this range. Most volume is coming from{" "}
                              <span className="font-semibold text-white">
                                {busiestShop?.shopName || "—"}
                              </span>{" "}
                              and{" "}
                              <span className="font-semibold text-white">
                                {secondBusiestShop?.shopName || "—"}
                              </span>
                              , so these shops should remain the top focus for operations, staffing,
                              and dispatch readiness.
                            </p>
                          </div>

                          <div className="hidden md:flex h-16 w-16 rounded-2xl bg-white/15 border border-white/25 items-center justify-center backdrop-blur-md shadow-lg">
                            <ArrowUpRight className="w-7 h-7 text-white" />
                          </div>
                        </div>

                        <div className="relative z-10 grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                          <div className="rounded-2xl bg-white/14 backdrop-blur-md p-4 border border-white/20 shadow-sm">
                            <p className="text-xs uppercase tracking-wide text-white/75">Shops Covered</p>
                            <p className="text-2xl font-bold mt-2">{safeRestaurantData.length}</p>
                          </div>

                          <div className="rounded-2xl bg-white/14 backdrop-blur-md p-4 border border-white/20 shadow-sm">
                            <p className="text-xs uppercase tracking-wide text-white/75">Drivers Active</p>
                            <p className="text-2xl font-bold mt-2">{safeDriverTaskAnalytics.length}</p>
                          </div>

                          <div className="rounded-2xl bg-white/14 backdrop-blur-md p-4 border border-white/20 shadow-sm">
                            <p className="text-xs uppercase tracking-wide text-white/75">Avg / Shop</p>
                            <p className="text-2xl font-bold mt-2">{avgTasksPerShop}</p>
                          </div>

                          <div className="rounded-2xl bg-white/14 backdrop-blur-md p-4 border border-white/20 shadow-sm">
                            <p className="text-xs uppercase tracking-wide text-white/75">Avg / Driver</p>
                            <p className="text-2xl font-bold mt-2">{avgTasksPerDriver}</p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white rounded-2xl border shadow-sm p-5">
                        <div className="flex items-center gap-2 mb-4">
                          <CircleAlert className="w-5 h-5 text-orange-600" />
                          <h3 className="text-lg font-semibold text-gray-900">Attention Points</h3>
                        </div>

                        <div className="space-y-3 text-sm">
                          <div className="rounded-xl bg-orange-50 border border-orange-100 p-3">
                            <p className="font-medium text-orange-800">Top shop load</p>
                            <p className="text-orange-700 mt-1">
                              {busiestShop?.shopName || "—"} handles {busiestShop?.taskCount || 0} tasks.
                            </p>
                          </div>

                          <div className="rounded-xl bg-amber-50 border border-amber-100 p-3">
                            <p className="font-medium text-amber-800">Volume concentration</p>
                            <p className="text-amber-700 mt-1">
                              Top 2 shops contribute {top2ShopShare}% of mapped shop volume.
                            </p>
                          </div>

                          <div className="rounded-xl bg-red-50 border border-red-100 p-3">
                            <p className="font-medium text-red-800">Cancellation watch</p>
                            <p className="text-red-700 mt-1">
                              {cancelledTasks} tasks are cancelled in this date range.
                            </p>
                          </div>

                          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                            <p className="font-medium text-slate-800">Data hygiene</p>
                            <p className="text-slate-600 mt-1">
                              {unknownShopCount} shop rows and {unknownDriverCount} driver rows have incomplete mapping.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Badges */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                      <div className="bg-white rounded-2xl border shadow-sm p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-11 h-11 rounded-xl bg-green-50 flex items-center justify-center">
                            <Medal className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Top Performer</p>
                            <p className="text-base font-semibold text-gray-900">
                              {topPerformerDriver?.driverName || "—"}
                            </p>
                            <p className="text-xs text-gray-500">
                              {topPerformerDriver?.taskCount || 0} tasks
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white rounded-2xl border shadow-sm p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-11 h-11 rounded-xl bg-orange-50 flex items-center justify-center">
                            <ShieldAlert className="w-5 h-5 text-orange-600" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Lowest Performer</p>
                            <p className="text-base font-semibold text-gray-900">
                              {lowestPerformerDriver?.driverName || "—"}
                            </p>
                            <p className="text-xs text-gray-500">
                              {lowestPerformerDriver?.taskCount || 0} tasks
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white rounded-2xl border shadow-sm p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-11 h-11 rounded-xl bg-red-50 flex items-center justify-center">
                            <FileWarning className="w-5 h-5 text-red-600" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Unknown Mapping</p>
                            <p className="text-base font-semibold text-gray-900">
                              {unknownShopCount + unknownDriverCount} rows
                            </p>
                            <p className="text-xs text-gray-500">
                              Incomplete shop or driver details
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Status + Top Shops */}
                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-5">
                    <div className="xl:col-span-2 bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
    <div>
      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
        <Activity className="w-5 h-5 text-orange-600" />
        Status Breakdown
      </h3>
      <p className="text-xs text-gray-500 mt-1">
        Clear count, percentage and operational health by status
      </p>
    </div>

    <div className="rounded-xl bg-orange-50 px-3 py-2 text-right border border-orange-100">
      <p className="text-[11px] text-orange-600 font-medium">Total Tasks</p>
      <p className="text-lg font-bold text-orange-700">{totalStatusTasks}</p>
    </div>
  </div>

  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
    {statusSummaryCards.map((card) => {
      const Icon = card.Icon;

      return (
        <div
          key={card.label}
          className="rounded-2xl border p-4"
          style={{ backgroundColor: card.bg, borderColor: card.bg }}
        >
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-medium text-gray-600">{card.label}</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {card.value}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                {card.percentage}% of total
              </p>
            </div>

            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center bg-white"
              style={{ color: card.color }}
            >
              <Icon className="w-5 h-5" />
            </div>
          </div>
        </div>
      );
    })}
  </div>

  {statusDisplayRows.length === 0 ? (
    <div className="h-56 flex items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-gray-50">
      <div className="text-center">
        <AlertCircle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
        <p className="text-sm font-medium text-gray-700">No status data found</p>
        <p className="text-xs text-gray-500 mt-1">
          Try changing the selected date range
        </p>
      </div>
    </div>
  ) : (
    <div className="space-y-3">
      {statusDisplayRows.map((item) => {
        const Icon = item.tone.Icon;

        return (
          <div
            key={item.taskStatus}
            className="rounded-2xl border p-4 transition hover:shadow-sm"
            style={{ borderColor: item.tone.border, backgroundColor: "#ffffff" }}
          >
            <div className="flex items-center justify-between gap-4 mb-3">
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{
                    backgroundColor: item.tone.bg,
                    color: item.tone.color,
                  }}
                >
                  <Icon className="w-5 h-5" />
                </div>

                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {formatStatusLabel(item.taskStatus)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {item.tone.label}
                    {topStatus?.taskStatus === item.taskStatus
                      ? " • Highest volume"
                      : ""}
                  </p>
                </div>
              </div>

              <div className="text-right shrink-0">
                <p className="text-lg font-bold text-gray-900">
                  {item.taskCount}
                </p>
                <p className="text-xs font-medium" style={{ color: item.tone.color }}>
                  {item.percentage}%
                </p>
              </div>
            </div>

            <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${item.percentage}%`,
                  backgroundColor: item.tone.color,
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  )}
</div>

                      <div className="bg-white rounded-2xl border shadow-sm p-5">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                            <Store className="w-5 h-5 text-orange-600" />
                            Top Shops
                          </h3>
                          <span className="text-xs text-gray-500">By task count</span>
                        </div>

                        <div className="space-y-3">
                          {topShops.map((shop, index) => (
                            <div
                              key={shop.id}
                              className="rounded-xl border p-3 hover:bg-orange-50 transition"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <p className="text-sm font-semibold text-gray-900">
                                    #{index + 1} {shop.shopName}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    Code: {shop.shopCode}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-lg font-bold text-gray-900">
                                    {shop.taskCount}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {getShopShare(shop.taskCount)}%
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="h-64 mt-5">
                          <Pie
                            data={restaurantPieChartData}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              plugins: { legend: { position: "bottom" } },
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Top Drivers + Driver Chart */}
                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-5">
                      <div className="bg-white rounded-2xl border shadow-sm p-5">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                            <Trophy className="w-5 h-5 text-orange-600" />
                            Top Drivers
                          </h3>
                          <span className="text-xs text-gray-500">Highest task volume</span>
                        </div>

                        <div className="space-y-3">
                          {topDrivers.map((driver, index) => (
                            <div
                              key={driver.id}
                              className="rounded-xl border p-3 hover:bg-orange-50 transition"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <p className="text-sm font-semibold text-gray-900">
                                    #{index + 1} {driver.driverName}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    {driver.driverCode} • {driver.driverMobile}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-lg font-bold text-gray-900">
                                    {driver.taskCount}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {getDriverShare(driver.taskCount)}%
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="xl:col-span-2 bg-white rounded-2xl border shadow-sm p-5">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                            <Truck className="w-5 h-5 text-orange-600" />
                            Driver Contribution
                          </h3>
                          <span className="text-xs text-gray-500">Top 5 comparison</span>
                        </div>

                        <div className="h-80">
                          <Bar
                            data={driverBarChartData}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              plugins: { legend: { display: false } },
                              scales: {
                                x: {
                                  ticks: { color: "#475569" },
                                  grid: { display: false },
                                },
                                y: {
                                  ticks: { color: "#475569" },
                                  grid: { color: "#e5e7eb" },
                                },
                              },
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Full Tables */}
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                      <div className="bg-white rounded-2xl border shadow-sm p-5">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">All Shops Ranking</h3>
                            <p className="text-xs text-gray-500 mt-1">
                              {filteredShopRows.length} visible of {safeRestaurantData.length} shops
                            </p>
                          </div>

                          <div className="flex flex-col sm:flex-row gap-2">
                            <div className="relative">
                              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                              <input
                                type="text"
                                value={shopSearch}
                                onChange={(e) => setShopSearch(e.target.value)}
                                placeholder="Search shop or code..."
                                className="h-10 pl-9 pr-3 border rounded-xl text-sm w-full sm:w-52 outline-none focus:ring-2 focus:ring-orange-200"
                              />
                            </div>

                            <div className="relative">
                              <ArrowUpDown className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                              <select
                                value={shopSortBy}
                                onChange={(e) => setShopSortBy(e.target.value)}
                                className="h-10 pl-9 pr-3 border rounded-xl text-sm w-full sm:w-44 outline-none focus:ring-2 focus:ring-orange-200 bg-white"
                              >
                                <option value="tasks_desc">Tasks: High to Low</option>
                                <option value="tasks_asc">Tasks: Low to High</option>
                                <option value="name_asc">Name: A to Z</option>
                                <option value="name_desc">Name: Z to A</option>
                              </select>
                            </div>
                          </div>
                        </div>

                        <div className="overflow-auto max-h-[480px] rounded-xl border">
                          <table className="min-w-full text-sm">
                            <thead className="sticky top-0 bg-orange-50 z-10">
                              <tr className="text-left border-b">
                                <th className="px-3 py-3 font-semibold text-gray-700">#</th>
                                <th className="px-3 py-3 font-semibold text-gray-700">Shop</th>
                                <th className="px-3 py-3 font-semibold text-gray-700">Code</th>
                                <th className="px-3 py-3 font-semibold text-gray-700 text-right">Tasks</th>
                                <th className="px-3 py-3 font-semibold text-gray-700 text-right">Share</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredShopRows.length === 0 ? (
                                <tr>
                                  <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                                    No shop data found
                                  </td>
                                </tr>
                              ) : (
                                filteredShopRows.map((shop, index) => (
                                  <tr key={shop.id} className="border-b last:border-b-0 hover:bg-orange-50/40">
                                    <td className="px-3 py-3">{index + 1}</td>
                                    <td className="px-3 py-3 font-medium text-gray-900 whitespace-nowrap">
                                      {shop.shopName}
                                    </td>
                                    <td className="px-3 py-3 text-gray-500 whitespace-nowrap">{shop.shopCode}</td>
                                    <td className="px-3 py-3 text-right font-semibold whitespace-nowrap">
                                      {shop.taskCount}
                                    </td>
                                    <td className="px-3 py-3 text-right text-gray-500 whitespace-nowrap">
                                      {getShopShare(shop.taskCount)}%
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div className="bg-white rounded-2xl border shadow-sm p-5">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">All Drivers Ranking</h3>
                            <p className="text-xs text-gray-500 mt-1">
                              {filteredDriverRowsPackky.length} visible of {safeDriverTaskAnalytics.length} drivers
                            </p>
                          </div>

                          <div className="flex flex-col sm:flex-row gap-2">
                            <div className="relative">
                              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                              <input
                                type="text"
                                value={driverSearchPackky}
                                onChange={(e) => setDriverSearchPackky(e.target.value)}
                                placeholder="Search driver, code, mobile..."
                                className="h-10 pl-9 pr-3 border rounded-xl text-sm w-full sm:w-56 outline-none focus:ring-2 focus:ring-orange-200"
                              />
                            </div>

                            <div className="relative">
                              <ArrowUpDown className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                              <select
                                value={driverSortByPackky}
                                onChange={(e) => setDriverSortByPackky(e.target.value)}
                                className="h-10 pl-9 pr-3 border rounded-xl text-sm w-full sm:w-44 outline-none focus:ring-2 focus:ring-orange-200 bg-white"
                              >
                                <option value="tasks_desc">Tasks: High to Low</option>
                                <option value="tasks_asc">Tasks: Low to High</option>
                                <option value="name_asc">Name: A to Z</option>
                                <option value="name_desc">Name: Z to A</option>
                              </select>
                            </div>
                          </div>
                        </div>

                        <div className="overflow-auto max-h-[480px] rounded-xl border">
                          <table className="min-w-full text-sm">
                            <thead className="sticky top-0 bg-orange-50 z-10">
                              <tr className="text-left border-b">
                                <th className="px-3 py-3 font-semibold text-gray-700">#</th>
                                <th className="px-3 py-3 font-semibold text-gray-700">Driver</th>
                                <th className="px-3 py-3 font-semibold text-gray-700">Code</th>
                                <th className="px-3 py-3 font-semibold text-gray-700">Mobile</th>
                                <th className="px-3 py-3 font-semibold text-gray-700 text-right">Tasks</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredDriverRowsPackky.length === 0 ? (
                                <tr>
                                  <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                                    No driver data found
                                  </td>
                                </tr>
                              ) : (
                                filteredDriverRowsPackky.map((driver, index) => (
                                  <tr key={driver.id} className="border-b last:border-b-0 hover:bg-orange-50/40">
                                    <td className="px-3 py-3">{index + 1}</td>
                                    <td className="px-3 py-3 font-medium text-gray-900 whitespace-nowrap">
                                      {driver.driverName}
                                    </td>
                                    <td className="px-3 py-3 text-gray-500 whitespace-nowrap">{driver.driverCode}</td>
                                    <td className="px-3 py-3 text-gray-500 whitespace-nowrap">{driver.driverMobile}</td>
                                    <td className="px-3 py-3 text-right font-semibold whitespace-nowrap">
                                      {driver.taskCount}
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-lg w-[90%] max-w-sm p-5">
            <h3 className="text-lg font-semibold mb-2">Open Driver Report</h3>

            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to open{" "}
              <span className="font-semibold capitalize">
                {pendingAction === "analytics" ? "analytics" : "report"}
              </span>{" "}
              for{" "}
              <span className="font-medium">{selectedDriver?.fullName}</span>?
            </p>

            <div className="flex justify-end gap-3">
              {/* Cancel */}
              <button
                onClick={() => {
                  setConfirmOpen(false);
                  setSelectedDriver(null);
                }}
                className="px-4 py-2 text-sm rounded border hover:"
              >
                Cancel
              </button>

              {/* Open */}
              <button
                onClick={handleOpen}
                className="px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700"
              >
                Open
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReportsDashboard;
